/**
 * Firebase Auth wiring.
 *
 * Firebase proves identity; Postgres decides authorisation (Spec §3). Nothing
 * here reads a role: the client must never decide what it is allowed to see,
 * because custom claims go stale until the next token refresh and, more
 * importantly, a client-side check is not a control.
 *
 * MFA for the address role and tenant admins is review finding F4. It is enforced
 * at the API by inspecting the token's second-factor claim; this module only has
 * to make sure a second factor can be presented.
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  onIdTokenChanged,
  reload,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  type Auth,
  type User,
} from 'firebase/auth';

import type { Config } from './config';

export interface Session {
  readonly app: FirebaseApp;
  readonly auth: Auth;
  /** Current ID token, refreshed transparently. Null when signed out. */
  getIdToken: () => Promise<string | null>;
  onChange: (listener: (user: User | null) => void) => () => void;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  /** `name` is optional on the shared contract -- the invitation-acceptance
   *  flow (`src/app/tenant/accept/InlineCredential.tsx`) signs up with no name
   *  field of its own. The sign-up screen (`src/app/routes/sign-up.tsx`)
   *  always passes one, set as Firebase's `displayName` before the first
   *  `/control/me` call so the menu has it on first render. */
  signUp: (email: string, password: string, name?: string) => Promise<void>;
  sendEmailVerification: () => Promise<void>;
  /** `reload()` then `getIdToken(true)`. BOTH: the client's own `emailVerified`
   *  comes from the first, and `api` reads the claim off the TOKEN, which only
   *  the second replaces. */
  refreshVerification: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

export function createSession(config: Config): Session {
  const app = initializeApp(config.firebase);
  const auth = getAuth(app);

  return {
    app,
    auth,
    getIdToken: async () => {
      const user = auth.currentUser;
      return user ? user.getIdToken() : null;
    },
    onChange: (listener) => onIdTokenChanged(auth, listener),
    signIn: async (email, password) => {
      await signInWithEmailAndPassword(auth, email, password);
    },
    signInWithGoogle: async () => {
      await signInWithPopup(auth, new GoogleAuthProvider());
    },
    signUp: async (email, password, name) => {
      const credential = await createUserWithEmailAndPassword(auth, email, password);
      if (name !== undefined) {
        await updateProfile(credential.user, { displayName: name });
      }
      await sendEmailVerification(credential.user);
    },
    sendEmailVerification: async () => {
      const user = auth.currentUser;
      if (user) await sendEmailVerification(user);
    },
    refreshVerification: async () => {
      const user = auth.currentUser;
      if (user) {
        await reload(user);
        await user.getIdToken(true);
      }
    },
    sendPasswordReset: async (email) => {
      await sendPasswordResetEmail(auth, email);
    },
    signOut: async () => {
      await signOut(auth);
    },
  };
}

/**
 * Firebase's error codes, mapped once and never surfaced raw: `auth/user-not-found`
 * is not a sentence a tenant reads, and it is also an oracle -- it tells whoever is
 * at the login form which addresses have accounts. `invalid-credentials` is
 * deliberately the landing spot for both a wrong password and an unknown address
 * (and for a malformed or disabled one -- each of those also answers "does this
 * address have an account?" if surfaced on its own), so the sign-in form cannot be
 * used to enumerate them.
 *
 * Kept a plain function over `Session`'s methods rather than a method on it:
 * `toAuthFailure` has no Firebase Auth instance to close over, so it does not
 * belong to any one session and every route component calls it directly on
 * whatever a `Session` method's promise rejected with.
 */
export type AuthFailure =
  | 'invalid-credentials'
  | 'email-in-use'
  | 'weak-password'
  | 'popup-closed'
  | 'network'
  | 'too-many-requests'
  | 'account-exists'
  | 'unknown';

/** Structural narrowing rather than `instanceof FirebaseError`: every throwable
 *  this module's callers actually see -- a real `FirebaseError`, a plain `Error`,
 *  a test double -- is handled by the same check, and `unknown` covers a `catch`
 *  clause's real static type. */
function firebaseCodeOf(error: unknown): string | null {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const code = (error as { code: unknown }).code;
    return typeof code === 'string' ? code : null;
  }
  return null;
}

/** `auth/invalid-credential` is the code the current Identity Toolkit SDK
 *  returns for a failed email/password sign-in in place of the older, separately
 *  named `wrong-password`/`user-not-found` pair; both older codes are kept mapped
 *  too since a pinned SDK version may still emit them. `invalid-email` and
 *  `user-disabled` are folded into the same bucket for the reason above: each one
 *  answers the account-enumeration question on its own if it is not. */
const FAILURE_BY_CODE: ReadonlyMap<string, AuthFailure> = new Map([
  ['auth/wrong-password', 'invalid-credentials'],
  ['auth/user-not-found', 'invalid-credentials'],
  ['auth/invalid-credential', 'invalid-credentials'],
  ['auth/invalid-email', 'invalid-credentials'],
  ['auth/user-disabled', 'invalid-credentials'],
  ['auth/email-already-in-use', 'email-in-use'],
  ['auth/weak-password', 'weak-password'],
  ['auth/popup-closed-by-user', 'popup-closed'],
  ['auth/cancelled-popup-request', 'popup-closed'],
  ['auth/popup-blocked', 'popup-closed'],
  ['auth/network-request-failed', 'network'],
  ['auth/too-many-requests', 'too-many-requests'],
  // Only reachable after the Google popup has proven the caller owns the
  // address, so saying a password account holds it answers nothing an
  // enumerator could not already see.
  ['auth/account-exists-with-different-credential', 'account-exists'],
]);

/** Maps whatever a `Session` method's promise rejected with onto the fixed
 *  failure taxonomy above. A code this table does not name -- `auth/internal-error`
 *  included -- lands on `'unknown'`, never on the code itself, so nothing that
 *  renders this return value can leak a Firebase implementation detail.
 *
 *  The unmapped code goes to the console, and nothing else: the generic copy
 *  says nothing about the cause, and without the log a misconfigured provider is
 *  undiagnosable. The error object itself may not go -- a `FirebaseError` carries
 *  the address that was typed in `customData.email` and interpolates it into
 *  `message`, so logging the object puts personal data in every console, session
 *  recorder and log collector that reads one. A throwable carrying no code has
 *  nothing safe to say, so it says nothing. */
export function toAuthFailure(error: unknown): AuthFailure {
  const code = firebaseCodeOf(error);
  const failure = (code === null ? undefined : FAILURE_BY_CODE.get(code)) ?? 'unknown';
  if (failure === 'unknown' && code !== null) {
    console.error('Sign-in failed with an unmapped error:', code);
  }
  return failure;
}
